import React from "react";
import { PageSeo } from "@/components/seo/PageSeo";
import { LegalPage } from "@/components/layout/LegalPage";

/**
 * Frac Pte. Ltd. Privacy Policy — verbatim from client-supplied draft.
 * Do not paraphrase; only structural markup is applied here.
 *
 * Any un-numbered continuation lines under a numbered list item in the
 * source are rendered as plain paragraphs indented under that item — they
 * are NOT re-numbered. See the comments above the two `<ol>` blocks in the
 * "How We Use Information" section for the flagged items.
 */

const LINK_CLS =
  "font-semibold text-frac-purple underline underline-offset-2 decoration-frac-purple/40 hover:text-frac-violet hover:decoration-frac-violet transition-colors duration-fast ease-out";

const LEGAL_NOTICE_STYLE = {
  color: "var(--frac-black)",
  fontWeight: 600,
};

// Definition-style list items shared between the "Personal Data" and
// "Disclosure" sections.
function TermList({ items }) {
  return (
    <dl className="space-y-4">
      {items.map(({ term, definition }) => (
        <div key={term}>
          <dt className="inline font-bold text-frac-black">{term}</dt>
          <dd className="inline text-frac-gray-70">
            {": "}
            {definition}
          </dd>
        </div>
      ))}
    </dl>
  );
}

const PERSONAL_DATA_ITEMS = [
  {
    term: "Contact information",
    definition: "Include but not limited to email addresses.",
  },
  {
    term: "Financial information",
    definition:
      "Include but not limited to Blockchain wallet addresses, crypto asset information, transaction history and trading data.",
  },
  {
    term: "Transaction information",
    definition:
      "Any information about the transaction which you've made on our Platform such as the type of transaction, transaction amount and timestamp.",
  },
  {
    term: "Communication",
    definition:
      "Include but not limited to feedbacks, questionnaires, survey responses and information provided to our support team via our official communication channels.",
  },
  {
    term: "Online identifiers",
    definition:
      "Include but not limited to geographical location, browser fingerprints, browser type & version, and IP addresses.",
  },
  {
    term: "Usage data",
    definition:
      "Include but not limited to user preferences and data collected via cookies or similar technologies.",
  },
  {
    term: "Information from 3rd party sources",
    definition:
      "Include but not limited to information about you from any other sources as required or permitted by applicable law, such as public databases. We may combine the information collected in order to comply with our legal obligations and limit the use of our services in connection with fraudulent or any other illicit activities.",
  },
];

const DISCLOSURE_ITEMS = [
  {
    term: "With your consent",
    definition:
      "Sharing your personal information with others for their own marketing uses and will be subjected to their privacy policies.",
  },
  {
    term: "To comply with legal obligations",
    definition:
      "To cooperate with the investigation of relevant government regulators, government ministries, statutory boards or authorities and/or law enforcement agencies, and other applicable governmental agencies, to comply with any directions, laws, rules, guidelines, regulations or schemes issued or administered by any of them, or we believe in good faith that the disclosure of personal information is necessary to prevent harm to another person, to report suspected illicit activities, to investigate violations of our User Agreement or any other applicable policies.",
  },
  {
    term: "With 3rd party service providers",
    definition:
      "Who help to facilitate business and compliance operations such as technology and marketing services.",
  },
  {
    term: "During a change to our business",
    definition:
      "Facilitating business asset transactions (which may extend to any mergers, acquisitions, dissolution, bankruptcy or asset sales) involving Frac or any of its affiliates subject to standard confidentiality arrangements.",
  },
  {
    term: "Anonymized Data",
    definition: "Sharing anonymized data with others for their own uses.",
  },
];

const sections = [
  {
    id: "applicability-of-this-policy",
    heading: "Applicability of this Policy",
    tocLabel: "Applicability",
    body: (
      <>
        <p>
          This Privacy Policy is part of and incorporated into the Terms and
          Conditions for the Site ("Terms and Conditions"), which govern
          your use of the Site and Services. Please review the Terms and
          Conditions prior to using the Site. Capitalized terms used but not
          otherwise defined herein shall have the meanings ascribed to them
          in the Terms and Conditions.
        </p>
        <p style={LEGAL_NOTICE_STYLE}>
          USE OF OUR SITE OR PROVISION OF ANY PERSONAL INFORMATION
          CONSTITUTES AGREEMENT TO THIS PRIVACY POLICY. PLEASE DO NOT USE
          THE SITE OR PROVIDE INFORMATION (OR ALLOW OTHERS TO DO SO ON YOUR
          BEHALF) IF YOU (AND YOUR PRINCIPAL IF YOU ARE ACTING AS AGENT) DO
          NOT AGREE WITH ALL OF THE TERMS OF USE INCLUDING THIS PRIVACY
          POLICY.
        </p>
        <p>
          This Privacy Policy supplements but does not supersede nor
          replace any other consents you may have previously provided to
          Frac in respect of your Personal Data, and your consents herein
          are additional to any rights which Frac may have at law to
          collect, use or disclose your Personal Data.
        </p>
        <p>
          Frac may from time to time update this Privacy Policy to ensure
          that this Privacy Policy is consistent with our future
          developments, industry trends and/or any changes in legal or
          regulatory requirements. Subject to your rights at law, you agree
          to be bound by the prevailing terms of this Privacy Policy as
          updated from time to time on our website. Please check back
          regularly for updated information on the handling of your
          Personal Data.
        </p>
      </>
    ),
  },
  {
    id: "personal-data-we-may-collect",
    heading: "Personal Data We May Collect",
    tocLabel: "Personal Data",
    body: <TermList items={PERSONAL_DATA_ITEMS} />,
  },
  {
    id: "how-we-collect-personal-data",
    heading: "How We Collect Personal Data",
    tocLabel: "How We Collect",
    body: (
      <>
        <p>Generally, Personal Data are collected in the following ways:</p>
        <ol className="ml-6 list-decimal space-y-3">
          <li>
            When you activate an account on the Site in order to get access
            to the Platform.
          </li>
          <li>
            When you use our electronic services or interact with us via
            the Site or use services on the Site.
          </li>
          <li>
            When you submit any form, including but not limited to
            application, declaration, proposal, or referral forms.
          </li>
          <li>
            When you enter into any agreement or provide other
            documentation or information in respect of your interactions
            with or transactions on the Platform.
          </li>
          <li>
            When you request that we contact you, be included in an email
            or other mailing list; or when you respond to our request for
            additional Personal Data, our promotions, and other
            initiatives.
          </li>
          <li>
            When you are contacted by, and respond to, our marketing
            representatives and agents and other service providers.
          </li>
        </ol>
      </>
    ),
  },
  {
    id: "how-we-use-information",
    heading: "How We Use Information",
    tocLabel: "How We Use",
    body: (
      <>
        <p>
          Generally, Frac uses and discloses your Personal Data for the
          following purposes:
        </p>
        {/* source formatting preserved from client-supplied draft; may need list-cleanup review */}
        <ol className="ml-6 list-decimal space-y-3">
          <li>
            Responding to, processing, and handling your complaints,
            queries, requests, feedback, and suggestions.
            <p className="mt-2">verifying your identity.</p>
          </li>
          <li>Create and update user accounts.</li>
          <li>Processing transactions.</li>
          <li>
            Managing the administrative and business operations of Frac
            and the Platform and complying with internal policies and
            procedures.
          </li>
          <li>
            Facilitating business asset transactions (which may extend to
            any mergers, acquisitions, or asset sales) involving Frac or
            any of its affiliates.
          </li>
          <li>
            Matching of any Personal Data held which relates to you for
            any of the purposes listed herein.
            <p className="mt-2">
              requesting feedback or participation in surveys, as well as
              conducting market research and/or analysis for statistical,
              profiling, or other purposes for us to improve our products
              and services, understand customer behavior, preferences and
              market trends, and to review, develop and improve the
              quality of our Platform and Services, including but not
              limited to preventing, detecting, and investigating crime,
              fraud and other illicit activities.
            </p>
          </li>
          <li>Providing media announcements and responses.</li>
          <li>Organizing promotional events.</li>
          <li>
            In connection with any claims, actions or proceedings
            (including but not limited to drafting and reviewing
            documents, transaction documentation, obtaining legal advice,
            and facilitating dispute resolution), and/or protecting and
            enforcing our contractual and legal rights and obligations.
          </li>
          <li>
            Complying with any applicable rules, laws and regulations,
            codes of practice or guidelines or to assist in law
            enforcement and investigations by relevant authorities;
            and/or
            <p className="mt-2">
              any other purpose relating to any of the above. These
              purposes may also apply even if you do not maintain any
              account(s) with us or have terminated these account(s).
            </p>
          </li>
        </ol>

        <p>
          In addition, where permitted under the applicable law, Frac may
          also collect, use and disclose your Personal Data for the
          following purposes (which we may describe in our documents and
          agreements as "Additional Purposes" for the handling of Personal
          Data):
        </p>
        {/* source formatting preserved from client-supplied draft; may need list-cleanup review */}
        <ol className="ml-6 list-decimal space-y-3">
          <li>
            Providing or marketing services, products, and benefits to
            you, including promotions, loyalty, and reward programmes.
          </li>
          <li>
            Matching Personal Data with other data collected for other
            purposes and from other sources (including third parties) in
            connection with the provision or offering of products and
            services, whether by Frac or other third parties.
          </li>
          <li>
            Administering contests and competitions and personalizing your
            experience at Frac's touchpoints.
            <p className="mt-2">
              sending you details of products, services, special offers
              and rewards, either to our customers generally, or which we
              have identified may be of interest to you; and/or conducting
              market research, understanding, and analyzing customer
              behavior, location, preferences, and demographics for us to
              offer you products and services as well as special offers
              and marketing programmes which may be relevant to your
              preferences and profile.
            </p>
          </li>
        </ol>

        <p>
          In relation to products or services or in your interactions with
          us, we may also have specifically notified you of other purposes
          for which we collect, use or disclose your Personal Data. If so,
          we will collect, use and disclose your Personal Data for these
          additional purposes as well, unless we have specifically notified
          you otherwise.
        </p>
      </>
    ),
  },
  {
    id: "disclosure-of-personal-data",
    heading: "Disclosure of Personal Data",
    tocLabel: "Disclosure",
    body: (
      <>
        <p>Frac may share your information in the following circumstances:</p>
        <TermList items={DISCLOSURE_ITEMS} />
      </>
    ),
  },
  {
    id: "data-security",
    heading: "Data Security",
    tocLabel: "Data Security",
    body: (
      <>
        <p>
          We will take reasonable efforts to protect Personal Data in its
          possession or its control by making reasonable security
          arrangements to prevent unauthorized access, collection, use,
          disclosure, copying, modification, disposal, or similar risks.
          However, we cannot completely guarantee the security of any
          Personal Data we may have collected from or about you, or that,
          for example, no harmful code will enter the Site (for example
          viruses, bugs, trojan horses, spyware or adware).
        </p>
        <p>You should be aware of the risks associated with using the Site.</p>
        <p>
          We are not responsible for the intentional or criminal acts of
          third parties such as hackers or "phishers."
        </p>
        <p>
          While we strive to protect your Personal Data, we can not ensure
          the security of the information you transmit to us via the
          Internet or when you access the Platform via mobile phones, and
          we urge you to take every precaution to protect your Personal
          Data when you access such platforms.
        </p>
        <p>
          We are not liable for any damages resulting from any security
          breaches, on unauthorized and/or fraudulent use of your account.
        </p>
      </>
    ),
  },
  {
    id: "age-limitation",
    heading: "Age Limitation",
    tocLabel: "Age Limitation",
    body: (
      <p>
        To the extent prohibited by applicable laws, we do not allow the
        use of our Sites and Services by anyone below the age of 18 years
        old. We will take the necessary steps to close out any such
        accounts that were found to be unlawfully created with personal
        data, and with the possible extent of preventing users from
        continuing the use of our Services.
      </p>
    ),
  },
  {
    id: "contact-us",
    heading: "Contact Us",
    tocLabel: "Contact",
    body: (
      <>
        <p>
          Please contact us if you have any questions or feedback relating
          to your Personal Data or our Privacy Policy or if you want to
          withdraw your consent to any or all use of your Personal Data
          granted by you. You may email us at{" "}
          <a
            href="mailto:hello@frac.io"
            className={LINK_CLS}
            data-testid="privacy-contact-email"
          >
            hello@frac.io
          </a>
        </p>
        <p>
          In the event where you withdraw your consent to any or all use
          of your Personal Data, depending on the nature of your request,
          Frac may not be able to continue to provide its products or
          services to you or administer any contractual relationship
          already in place. This may also result in the termination of any
          agreements you have with Frac, and you being in breach of your
          contractual obligations or undertakings. Frac's legal rights and
          remedies in such event are expressly reserved.
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <>
      <PageSeo
        title="Privacy Policy — Frac"
        description="Frac Pte. Ltd. Privacy Policy — how we collect, use and disclose personal data. Last updated 3 November 2022."
        path="/privacy"
        image="/og/privacy.png"
      />
      <div data-testid="page-privacy">
        <LegalPage
          title="Privacy Policy"
          sections={sections}
          testId="page-privacy-body"
          lastUpdated="3rd November 2022"
          draftBanner={false}
          intro={
            <p>
              The Privacy Policy ("Privacy Policy") describes how Frac Pte.
              Ltd. ("Frac", "we", "us" or "our") may collect personal and
              identifying information, use and disclose such personal
              information. Please read this privacy policy (the "Privacy
              Policy") carefully.
            </p>
          }
        />
      </div>
    </>
  );
}
